import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'

import { getSSOLinks, mountUrl, post } from '@/api'
import { MFA_ERROR_MESSAGE } from '@/constants/errors'
import { useMixpanel } from '@/services/MixpanelService'

import { ENDPOINTS } from '../../api/endpoints'
import { REFRESH_TOKEN_KEY, TOKEN_KEY, USER_KEY } from '../../storage/constants'
import { saveDataInStorage } from '../../storage/storageRepository'

import type { LoginResponse } from '../../api/models/LoginResponse'
import type { RoutePageProps } from '../taskpane/types'

type LoginFormData = {
  email: string
  password: string
  useSSO: boolean
}

export const useLogin = ({ onUpdateRoute }: RoutePageProps) => {
  const isSSOEnabled = process.env.IS_SSO_ENABLED === 'true'
  const mixpanel = useMixpanel(true)
  const [shouldSendEvent, setShouldSendEvent] = useState(false)

  const methods = useForm<LoginFormData>({
    defaultValues: {
      email: '',
      password: '',
      useSSO: false,
    },
  })
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = methods

  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState<{
    text: string
    type: 'success' | 'error' | 'info'
  } | null>(null)
  const [showMFAInstructions, setShowMFAInstructions] = useState(false)

  useEffect(() => {
    if (mixpanel && shouldSendEvent) {
      mixpanel.track('Outlook Login', {
        email: methods.watch('email'),
      })
      setShouldSendEvent(false)
    }
  }, [mixpanel, shouldSendEvent])

  const onSubmit = async (formData: LoginFormData) => {
    setLoading(true)
    if (isSSOEnabled) {
      try {
        const response = await getSSOLinks(formData.email)
        if (response.ssoRequired && response?.links?.length > 0) {
          window.location.href = response.links[0].url
          return
        }
        // eslint-disable-next-line no-empty
      } catch (_err) {}
    }

    try {
      setShouldSendEvent(true)
      const requestData = {
        login: formData.email,
        password: formData.password,
        fromOutlook: true,
      }

      const response = await post<LoginResponse>(
        mountUrl(ENDPOINTS.LOGIN),
        requestData
      )

      if (response) {
        saveDataInStorage(TOKEN_KEY, response.token)
        saveDataInStorage(REFRESH_TOKEN_KEY, response.refreshToken)
        saveDataInStorage(USER_KEY, JSON.stringify(response.user))
      }

      onUpdateRoute()
    } catch (error) {
      if (error?.error === MFA_ERROR_MESSAGE) {
        setShowMFAInstructions(true)
      } else {
        setMessage({ text: 'Login failed. Please try again.', type: 'error' })
      }
    } finally {
      setLoading(false)
    }
  }

  const attemptToLogin = handleSubmit(onSubmit)

  return {
    errors,
    loading,
    message,
    methods,
    attemptToLogin,
    register,
    setMessage,
    isSSOEnabled,
    showMFAInstructions,
  }
}
